import { useId, useRef, useState, type FormEvent } from "react";
import logo from "../assets/urmate-logo.webp";
import { Spinner } from "../components/ui/Spinner.js";
import { api, ApiError } from "../lib/api.js";

/**
 * Ekran logowania wg design-system/snippets/login.html.
 *
 * L-10: widok jest jedna mala karta, wiec wolno go wysrodkowac i ograniczyc szerokosc.
 *       To WYJATEK dotyczacy tylko takich widokow - nie rozciagamy go na ekrany z wieloma
 *       kartami (tam obowiazuje L-8 i limit na kolumnie tresci).
 * L-5b: tytul ekranu to nazwa automatyzacji, ta sama co podpis obok logo w headerze.
 * F-12: `required` zdjete. Natywny dymek walidacji przegladarki jest zabroniony jako
 *       finalny sposob pokazania bledu - jest po angielsku i nieostylowany. Walidujemy
 *       sami, komunikat po polsku pod polem, powiazany przez aria-describedby (A-5).
 * B-3:  przycisk przestaje byc slabem na 100% poza telefonem.
 * R-1:  jeden promien karty na wszystkich progach. Gotowiec ma tu rozjazd (16px na
 *       telefonie, --radius-card od 640px), co lamie R-1.
 */

interface Props {
  onSignedIn: () => void | Promise<void>;
}

type Bledy = { username?: string; password?: string; ogolny?: string };

export function LoginPage({ onSignedIn }: Props): React.JSX.Element {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [bledy, setBledy] = useState<Bledy>({});
  const [busy, setBusy] = useState(false);

  const idUser = useId();
  const idPass = useId();
  const refUser = useRef<HTMLInputElement>(null);
  const refPass = useRef<HTMLInputElement>(null);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    // B-5: straznik w handlerze, a nie disabled na przycisku - disabled na
    // sfokusowanym przycisku przerzuca fokus na <body> i lamie A-4.
    if (busy) return;

    const nowe: Bledy = {};
    if (!username.trim()) nowe.username = "Podaj nazwę użytkownika.";
    if (!password) nowe.password = "Podaj hasło.";
    if (nowe.username || nowe.password) {
      setBledy(nowe);
      // A-4: fokus ladzie na pierwszym polu z bledem, zeby klawiatura nie musiala szukac.
      (nowe.username ? refUser : refPass).current?.focus();
      return;
    }

    setBusy(true);
    setBledy({});
    try {
      await api.login(username, password);
      await onSignedIn();
    } catch (err) {
      setBledy({
        ogolny:
          err instanceof ApiError && err.status === 401
            ? "Nieprawidłowa nazwa użytkownika lub hasło."
            : "Nie udało się zalogować. Spróbuj ponownie za chwilę.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-screen">
      <div className="login-card card">
        <img className="login-logo" src={logo} alt="urmate.ai" />
        <h1 className="login-title">ConvoLens</h1>
        <p className="login-lead">Zaloguj się, żeby analizować rozmowy.</p>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          {bledy.ogolny && (
            <p className="form-error" role="alert">
              {bledy.ogolny}
            </p>
          )}

          <div className="field">
            <label className="field-label" htmlFor={idUser}>
              Użytkownik
            </label>
            <input
              id={idUser}
              ref={refUser}
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              aria-invalid={bledy.username ? true : undefined}
              aria-describedby={bledy.username ? `${idUser}-blad` : undefined}
            />
            {bledy.username && (
              <span className="field-error" id={`${idUser}-blad`}>
                {bledy.username}
              </span>
            )}
          </div>

          <div className="field">
            <label className="field-label" htmlFor={idPass}>
              Hasło
            </label>
            <input
              id={idPass}
              ref={refPass}
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              aria-invalid={bledy.password ? true : undefined}
              aria-describedby={bledy.password ? `${idPass}-blad` : undefined}
            />
            {bledy.password && (
              <span className="field-error" id={`${idPass}-blad`}>
                {bledy.password}
              </span>
            )}
          </div>

          <button type="submit" className="btn btn--primary login-submit" aria-disabled={busy} aria-busy={busy}>
            {busy && <Spinner rozmiar="s" />}
            {busy ? "Loguję..." : "Zaloguj"}
          </button>
        </form>
      </div>
    </main>
  );
}
